// ============================================
// FILE: frontend/src/hooks/useMessages.js
// PURPOSE: Custom hook for in-app messaging with auto-polling
// FIXED: Polling now actually starts and runs automatically
// ============================================

import { useState, useCallback, useRef, useEffect } from 'react';
import { messagesAPI } from '../api/messages.api.js';

console.log('[useMessages] Initializing useMessages hook');

// ============================================
// MAIN HOOK
// ============================================

export const useMessages = () => {
  console.log('[useMessages] Hook created');

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [messages, setMessages] = useState([]);
  const [adminChat, setAdminChat] = useState(null);

  const [totalUnread, setTotalUnread] = useState(0);
  const [connectionUnread, setConnectionUnread] = useState({});

  const [loading, setLoading] = useState(false);
  const [sendingMessageId, setSendingMessageId] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);

  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    pages: 0
  });

  // Use ref to prevent duplicate requests and track polling state
  const pollingIntervalRef = useRef(null);
  const isPollingRef = useRef(false);

  // ========================================
  // SEND MESSAGE
  // ========================================

  const sendMessage = useCallback(async (connectionId, content) => {
    console.log('[useMessages] 💬 Sending message...', {
      connectionId,
      contentLength: content.length
    });

    if (!content || content.trim().length === 0) {
      console.error('[useMessages] ❌ Cannot send empty message');
      setError('Message cannot be empty');
      return;
    }

    if (content.length > 5000) {
      console.error('[useMessages] ❌ Message too long');
      setError('Message cannot exceed 5000 characters');
      return;
    }

    setSendingMessageId(connectionId);
    setError(null);

    try {
      const response = await messagesAPI.sendMessage(connectionId, content);
      console.log('[useMessages] ✅ Message sent:', {
        messageId: response.data?.id,
        createdAt: response.data?.createdAt
      });

      // Add to local messages
      setMessages(prev => [...prev, response.data]);
      return response;
    } catch (err) {
      console.error('[useMessages] ❌ Failed to send message:', err);
      const errorMsg = err.response?.data?.message || 'Failed to send message';
      setError(errorMsg);
      throw err;
    } finally {
      setSendingMessageId(null);
    }
  }, []);

  // ========================================
  // FETCH MESSAGES (CHAT HISTORY)
  // ========================================

  const fetchMessages = useCallback(async (connectionId, page = 1, limit = 50) => {
    console.log('[useMessages] 📜 Fetching messages...', {
      connectionId,
      page,
      limit
    });

    setLoadingChat(true);
    setError(null);

    try {
      const response = await messagesAPI.getMessages(connectionId, page, limit);
      console.log('[useMessages] ✅ Messages fetched:', {
        count: response.messages?.length,
        total: response.pagination?.total
      });

      setMessages(response.messages || []);
      setPagination(response.pagination || {});
      return response;
    } catch (err) {
      console.error('[useMessages] ❌ Failed to fetch messages:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load messages';
      setError(errorMsg);
      throw err;
    } finally {
      setLoadingChat(false);
    }
  }, []);

  // ========================================
  // GET UNREAD COUNT (SINGLE CONNECTION)
  // ========================================

  const fetchUnreadCount = useCallback(async (connectionId) => {
    console.log('[useMessages] 🔔 Fetching unread count...', { connectionId });

    try {
      const response = await messagesAPI.getUnreadCount(connectionId);
      console.log('[useMessages] ✅ Unread count retrieved:', {
        unreadCount: response.unreadCount
      });

      setConnectionUnread(prev => ({
        ...prev,
        [connectionId]: response.unreadCount
      }));

      return response;
    } catch (err) {
      console.error('[useMessages] ❌ Failed to fetch unread count:', err);
    }
  }, []);

  // ========================================
  // GET TOTAL UNREAD MESSAGES
  // ========================================

  const fetchTotalUnread = useCallback(async () => {
    console.log('[useMessages] 📊 Fetching total unread messages...');

    try {
      const response = await messagesAPI.getTotalUnreadMessages();
      console.log('[useMessages] ✅ Total unread retrieved:', {
        totalUnread: response.totalUnread,
        connectionsWithUnread: response.connectionsWithUnread?.length
      });

      setTotalUnread(response.totalUnread);

      // Update individual connection unreads
      const unreadMap = {};
      response.connectionsWithUnread?.forEach(conn => {
        unreadMap[conn.connectionId] = conn.unreadCount;
      });
      setConnectionUnread(unreadMap);

      return response;
    } catch (err) {
      console.error('[useMessages] ❌ Failed to fetch total unread:', err);
    }
  }, []);

  // ========================================
  // ADMIN VIEW CHAT
  // ========================================

  const adminViewChat = useCallback(async (connectionId) => {
    console.log('[useMessages] 🔍 Admin viewing chat...', { connectionId });
    setLoadingChat(true);
    setError(null);

    try {
      const response = await messagesAPI.adminViewChat(connectionId);
      console.log('[useMessages] ✅ Admin chat retrieved:', {
        messageCount: response.chat?.messageCount,
        landownerEmail: response.chat?.landowner?.email,
        investorEmail: response.chat?.investor?.email
      });

      setAdminChat(response.chat);
      return response;
    } catch (err) {
      console.error('[useMessages] ❌ Failed to view chat:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load conversation';
      setError(errorMsg);
      throw err;
    } finally {
      setLoadingChat(false);
    }
  }, []);

  // ========================================
  // START POLLING FOR NEW MESSAGES
  // ========================================

  const startPolling = useCallback((connectionId, interval = 180000) => {
    console.log('[useMessages] 🔄 Starting message polling...', {
      connectionId,
      interval
    });

    // Prevent duplicate polling
    if (isPollingRef.current) {
      console.log('[useMessages] ⚠️ Polling already active, skipping');
      return;
    }

    isPollingRef.current = true;

    // Clear existing interval
    if (pollingIntervalRef.current) {
      console.log('[useMessages] Clearing existing polling interval');
      clearInterval(pollingIntervalRef.current);
    }

    // Initial fetch
    console.log('[useMessages] Initial message fetch before polling starts');
    fetchMessages(connectionId).catch(err => {
      console.error('[useMessages] Initial fetch error:', err);
    });

    // Set up polling interval
    pollingIntervalRef.current = setInterval(async () => {
      console.log('[useMessages] ⏱️ Polling tick - fetching new messages');
      try {
        await fetchMessages(connectionId);
        console.log('[useMessages] ✅ Poll completed successfully');
      } catch (err) {
        console.error('[useMessages] ❌ Polling error:', err);
        // Continue polling even on error
      }
    }, interval);

    console.log('[useMessages] ✅ Polling started with interval:', interval);
  }, [fetchMessages]);

  // ========================================
  // STOP POLLING
  // ========================================

  const stopPolling = useCallback(() => {
    console.log('[useMessages] ⏹️ Stopping message polling...');
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('[useMessages] ✅ Polling interval cleared');
    }
    
    isPollingRef.current = false;
    console.log('[useMessages] ✅ Polling stopped');
  }, []);

  // ========================================
  // CLEANUP ON UNMOUNT
  // ========================================

  useEffect(() => {
    return () => {
      console.log('[useMessages] Cleanup: stopping polling on unmount');
      stopPolling();
    };
  }, [stopPolling]);

  // ========================================
  // RESET STATE
  // ========================================

  const resetState = useCallback(() => {
    console.log('[useMessages] 🔄 Resetting state...');
    setMessages([]);
    setAdminChat(null);
    setTotalUnread(0);
    setConnectionUnread({});
    setError(null);
    setLoading(false);
    setLoadingChat(false);
    setSendingMessageId(null);
    stopPolling();
  }, [stopPolling]);

  // ========================================
  // RETURN HOOK API
  // ========================================

  return {
    // State
    messages,
    adminChat,
    totalUnread,
    connectionUnread,
    loading,
    sendingMessageId,
    loadingChat,
    error,
    pagination,

    // Methods
    sendMessage,
    fetchMessages,
    fetchUnreadCount,
    fetchTotalUnread,
    getTotalUnreadMessages: fetchTotalUnread, // 👈 alias added
    adminViewChat,
    startPolling,
    stopPolling,
    resetState
  };
};

export default useMessages;